import React, { useState } from 'react';
import { X, Check } from 'lucide-react';

interface ImagePickerModalProps {
  images: string[];
  onSelect: (imageUrl: string) => void;
  onCancel: () => void;
  isOpen: boolean;
  title?: string;
  description?: string;
}

export function ImagePickerModal({
  images,
  onSelect,
  onCancel,
  isOpen,
  title = 'Kies je favoriete afbeelding',
  description = 'Selecteer de afbeelding die je wilt gebruiken',
}: ImagePickerModalProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (!isOpen) return null;

  const handleSelect = () => {
    if (selectedIndex !== null) {
      onSelect(images[selectedIndex]);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onCancel}
        />

        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
            <div>
              <h2 className="text-xl font-bold text-[#231F20]">{title}</h2>
              <p className="text-sm text-gray-600 mt-1">{description}</p>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {images.map((imageUrl, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedIndex(index)}
                  className={`
                    relative rounded-lg overflow-hidden border-2 transition-all
                    ${
                      selectedIndex === index
                        ? 'border-[#231F20] shadow-lg ring-2 ring-[#231F20] ring-opacity-50'
                        : 'border-gray-200 hover:border-[#231F20] hover:shadow-md'
                    }
                  `}
                >
                  <div className="aspect-video relative">
                    <img
                      src={imageUrl}
                      alt={`Option ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src =
                          'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="450"%3E%3Crect fill="%23e5e7eb" width="800" height="450"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" fill="%239ca3af" font-family="sans-serif" font-size="18"%3EAfbeelding niet beschikbaar%3C/text%3E%3C/svg%3E';
                      }}
                    />
                    {selectedIndex === index && (
                      <div className="absolute inset-0 bg-[#231F20] bg-opacity-20 flex items-center justify-center">
                        <div className="bg-white rounded-full p-2">
                          <Check className="w-6 h-6 text-[#231F20]" />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="absolute top-2 left-2 bg-white rounded-full px-3 py-1 text-sm font-semibold text-[#231F20] shadow-sm">
                    Optie {index + 1}
                  </div>
                </button>
              ))}
            </div>

            {images.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <p>Geen afbeeldingen beschikbaar</p>
              </div>
            )}
          </div>

          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
            <button onClick={onCancel} className="btn-secondary">
              Annuleren
            </button>
            <button
              onClick={handleSelect}
              disabled={selectedIndex === null}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Gebruik geselecteerde afbeelding
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
